import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  AddGroupMemberRequest, CreateGroupRequest, GroupMemberDto, MemberSearchResultDto, MyGroupDto,
  SpendGroupDetailDto, SpendGroupDto, ThresholdNotificationDto, UpdateGroupRequest
} from '../lib/types';

export function useGroups() {
  return useQuery({ queryKey: ['admin', 'groups'], queryFn: () => apiClient.get<SpendGroupDto[]>('/api/admin/groups') });
}

export function useGroup(id: number | null) {
  return useQuery({
    queryKey: ['admin', 'groups', id],
    queryFn: () => apiClient.get<SpendGroupDetailDto>(`/api/admin/groups/${id}`),
    enabled: id !== null
  });
}

export function useMemberSearch(q: string) {
  return useQuery({
    queryKey: ['admin', 'members', 'search', q],
    queryFn: () => apiClient.get<MemberSearchResultDto[]>(`/api/admin/members/search?q=${encodeURIComponent(q)}`),
    staleTime: 15_000
  });
}

export function useMyGroups() {
  return useQuery({ queryKey: ['me', 'groups'], queryFn: () => apiClient.get<MyGroupDto[]>('/api/me/groups'), staleTime: 60_000 });
}

/** All group mutations invalidate the admin group queries (and the member-facing view). */
export function useGroupMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['admin', 'groups'] }),
    qc.invalidateQueries({ queryKey: ['admin', 'notifications'] }),
    qc.invalidateQueries({ queryKey: ['me', 'groups'] })
  ]);

  const create = useMutation({
    mutationFn: (body: CreateGroupRequest) => apiClient.post<SpendGroupDto>('/api/admin/groups', body),
    onSuccess: invalidate
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateGroupRequest }) => apiClient.put<SpendGroupDto>(`/api/admin/groups/${id}`, body),
    onSuccess: invalidate
  });
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.del<void>(`/api/admin/groups/${id}`),
    onSuccess: invalidate
  });
  const addMember = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AddGroupMemberRequest }) => apiClient.post<GroupMemberDto>(`/api/admin/groups/${id}/members`, body),
    onSuccess: invalidate
  });
  const removeMember = useMutation({
    mutationFn: ({ id, memberId }: { id: number; memberId: number }) => apiClient.del<void>(`/api/admin/groups/${id}/members/${memberId}`),
    onSuccess: invalidate
  });
  const resend = useMutation({
    mutationFn: ({ id, thresholdPct }: { id: number; thresholdPct?: number }) =>
      apiClient.post<ThresholdNotificationDto>(`/api/admin/groups/${id}/notifications/resend`, { thresholdPct: thresholdPct ?? null }),
    onSuccess: invalidate
  });
  const sendStatus = useMutation({
    mutationFn: (id: number) => apiClient.post<ThresholdNotificationDto>(`/api/admin/groups/${id}/notifications/status`, {}),
    onSuccess: invalidate
  });

  return { create, update, remove, addMember, removeMember, resend, sendStatus };
}
